import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

const translations = {
  fr: {
    // Navigation
    nav: {
      solution: 'Solution',
      howItWorks: 'Comment ça marche',
      forBrands: 'Pour les marques',
      forUsers: 'Pour les utilisateurs',
      pricing: 'Tarifs',
      faq: 'FAQ'
    },
    
    // Hero
    hero: {
      headline: 'Transformez chaque dollar marketing en action mesurable.',
      subheadline: 'Les marques financent des missions. Les utilisateurs gagnent de l\'argent. AQERA transforme les budgets marketing en actions réelles, mesurables et récompensées.',
      cta: 'S\'inscrire et commencer l\'essai gratuit',
      credibility: 'Déjà adopté par des cafés, restaurants, commerces de détail, salons de beauté et bien d\'autres.'
    },
    
    // Problem
    problem: {
      title: 'Et si les marques arrêtaient de gaspiller de l\'argent ?',
      subtitle: 'Le marketing traditionnel est un modèle obsolète',
      items: [
        {
          title: 'Aucune garantie de résultat',
          description: 'Les commerces paient des publicités sans jamais savoir si elles génèrent de vraies actions.',
          solutionTitle: 'Paiement à la performance',
          solution: 'Paiement uniquement pour des actions validées et mesurables.'
        },
        {
          title: 'Traçabilité inexistante',
          description: 'Les campagnes manquent de données précises sur l\'engagement réel des consommateurs.',
          solutionTitle: 'Traçabilité totale',
          solution: 'Chaque action est traçable, vérifiable et quantifiable en temps réel.'
        },
        {
          title: 'Engagement superficiel',
          description: 'Les impressions et les clics ne se traduisent pas en comportements utiles.',
          solutionTitle: 'Engagement réel',
          solution: 'Des missions concrètes qui génèrent de vraies interactions.'
        },
        {
          title: 'Valeur non distribuée',
          description: 'Les consommateurs voient des publicités sans jamais être réellement valorisés pour leur attention.',
          solutionTitle: 'Valeur partagée',
          solution: 'Les utilisateurs sont directement récompensés pour leurs actions.'
        }
      ]
    },
    
    // Solution
    solution: {
      title: 'AQERA transforme le budget marketing en valeur réelle.',
      subtitle: 'Une nouvelle infrastructure économique',
      description: 'Nous connectons les budgets publicitaires des marques aux actions des consommateurs, créant une boucle économique locale plus intelligente.',
      highlight: 'Les marques paient pour de la visibilité. AQERA paie les utilisateurs pour des actions réelles.'
    },
    
    // How it works
    howItWorks: {
      title: 'Une boucle simple. Un impact réel.',
      subtitle: 'Comment fonctionne AQERA',
      steps: [
        {
          number: '01',
          title: 'Budget alloué',
          description: 'Un café, restaurant, salon de beauté ou commerce local définit son budget cartes-cadeaux sur AQERA.'
        },
        {
          number: '02',
          title: 'Missions créées',
          description: 'AQERA transforme ce budget en missions marketing ciblées et mesurables.'
        },
        {
          number: '03',
          title: 'Actions réalisées',
          description: 'Les utilisateurs effectuent des actions marketing utiles et vérifiables.'
        },
        {
          number: '04',
          title: 'Argent distribué',
          description: 'Les utilisateurs reçoivent de l\'argent directement dans leur wallet AQERA.'
        },
        {
          number: '05',
          title: 'Valeur locale',
          description: 'Ils utilisent leurs gains dans le marketplace de cartes-cadeaux partenaires.'
        }
      ]
    },
    
    // Revolutionary
    revolutionary: {
      title: 'AQERA ne diffuse pas seulement du marketing.',
      titleHighlight: 'AQERA le fait exécuter.',
      subtitle: 'Un changement de paradigme',
      comparison: {
        traditional: 'Publicité traditionnelle',
        aqera: 'AQERA',
        items: [
          { traditional: 'Paiement avant résultat', aqera: 'Budget orienté performance' },
          { traditional: 'Visibilité floue', aqera: 'Actions traçables' },
          { traditional: 'Audience passive', aqera: 'Participation active' },
          { traditional: 'Valeur perdue', aqera: 'Récompense distribuée' },
          { traditional: 'Dépense linéaire', aqera: 'Boucle économique locale' }
        ]
      }
    },
    
    // Dual Audience
    dualAudience: {
      consumers: {
        title: 'Pour les utilisateurs',
        headline: 'Transformez votre attention en valeur.',
        benefits: [
          'Réalisez des missions simples',
          'Gagnez de l\'argent réel',
          'Accédez à des récompenses utiles',
          'Utilisez vos gains dans un marketplace local',
          'Participez à une économie plus intelligente'
        ]
      },
      brands: {
        title: 'Pour les marques',
        headline: 'Transformez votre marketing en résultats mesurables.',
        benefits: [
          'Activez une audience locale',
          'Récompensez des actions utiles',
          'Maîtrisez vos budgets',
          'Mesurez la performance en temps réel',
          'Générez trafic, visibilité et conversion'
        ]
      }
    },
    
    // Use Case
    useCase: {
      title: 'Une boucle simple. Un impact réel.',
      subtitle: 'Un exemple concret',
      steps: [
        'Un commerce alloue un budget cartes-cadeaux (ex: 400$)',
        'AQERA crée des missions rémunérées',
        'Les utilisateurs réalisent des actions concrètes',
        'Ils reçoivent de l\'argent dans leur wallet',
        'Ils utilisent cette valeur chez les partenaires'
      ],
      results: {
        title: 'Résultat',
        metrics: [
          '40 cartes-cadeaux de 10$',
          '120 actions réalisées via les différents réseaux sociaux',
          'Des dizaines de clients exposés et engagés'
        ],
        benefits: [
          'Visibilité réelle',
          'Engagement mesurable',
          'Trafic local'
        ]
      }
    },
    
    // Product
    product: {
      title: 'Une plateforme technologique complète.',
      subtitle: 'Découvrez l\'interface AQERA',
      modules: {
        missions: {
          title: 'Missions',
          description: 'Découvrez et complétez des missions marketing rémunérées.'
        },
        wallet: {
          title: 'Wallet',
          description: 'Suivez vos gains et gérez votre argent en temps réel.'
        },
        history: {
          title: 'Historique',
          description: 'Consultez toutes vos missions complétées et transactions.'
        },
        marketplace: {
          title: 'Marketplace',
          description: 'Utilisez vos gains dans notre réseau de partenaires.'
        }
      }
    },
    
    // Advantages
    advantages: {
      title: 'Pourquoi AQERA peut changer le marketing local.',
      subtitle: 'Nos avantages stratégiques',
      items: [
        { title: 'Mesurable', description: 'Chaque action est traçable et quantifiable.' },
        { title: 'Scalable', description: 'Infrastructure conçue pour la croissance.' },
        { title: 'Transparent', description: 'Visibilité totale sur l\'utilisation des budgets.' },
        { title: 'Engagé', description: 'Des utilisateurs actifs, pas des spectateurs.' },
        { title: 'Local-first', description: 'Conçu pour l\'économie de proximité.' },
        { title: 'Performance-driven', description: 'Résultats concrets, pas des promesses.' }
      ]
    },
    
    // Vision
    vision: {
      title: 'La prochaine génération du marketing local.',
      description: 'AQERA veut redéfinir la relation entre budgets publicitaires, comportements consommateurs et commerce local. L\'ambition est de transformer une dépense marketing passive en un moteur de croissance plus intelligent, plus juste et plus mesurable.'
    },
    
    // Pricing
    pricing: {
      title: 'Un tarif simple et transparent.',
      subtitle: 'Pour les marques et restaurants',
      price: '49',
      currency: 'CAD',
      period: '/mois',
      trial: '7 jours d\'essai gratuit',
      features: [
        'Accès complet à la plateforme',
        'Création illimitée de missions',
        'Dashboard analytics avancé',
        'Support prioritaire',
        'Sans engagement'
      ],
      cta: 'Commencer l\'essai gratuit'
    },
    
    // FAQ
    faq: {
      title: 'Questions fréquentes',
      subtitle: 'Tout ce que vous devez savoir',
      items: [
        {
          question: 'AQERA est-elle une application de cashback classique ?',
          answer: 'Non. AQERA n\'est pas une app de cashback ou de coupons. C\'est une infrastructure marketing qui transforme les budgets publicitaires en missions rémunérées. Les utilisateurs sont récompensés pour des actions marketing utiles, pas pour de simples achats.'
        },
        {
          question: 'Comment les utilisateurs gagnent-ils de l\'argent ?',
          answer: 'Les utilisateurs complètent des missions marketing créées par les marques partenaires. Chaque mission validée génère une récompense en argent réel, directement créditée dans leur wallet AQERA.'
        },
        {
          question: 'Comment les commerces financent-ils les récompenses ?',
          answer: 'Les commerces allouent un budget marketing sur AQERA. Ce budget est transformé en missions. Ils ne paient que pour les actions réellement effectuées et validées.'
        },
        {
          question: 'À quoi sert le wallet ?',
          answer: 'Le wallet AQERA centralise tous les gains de l\'utilisateur. C\'est un portefeuille digital où s\'accumule l\'argent gagné via les missions.'
        },
        {
          question: 'Comment fonctionne le marketplace ?',
          answer: 'Le marketplace permet aux utilisateurs d\'utiliser leurs gains pour acheter des cartes-cadeaux chez nos partenaires locaux. Cela crée une boucle économique vertueuse au sein de l\'écosystème.'
        },
        {
          question: 'Pourquoi AQERA est-elle différente de la publicité traditionnelle ?',
          answer: 'La publicité traditionnelle paie pour de la visibilité sans garantie de résultat. AQERA paie uniquement pour des actions mesurables et validées. Le budget devient performance.'
        },
        {
          question: 'À qui s\'adresse AQERA ?',
          answer: 'AQERA s\'adresse aux cafés, restaurants, commerces de détail, épiceries, salons de beauté, barbiers et toutes les entreprises locales qui veulent optimiser leur budget marketing. Et aux consommateurs qui veulent être récompensés pour leur engagement.'
        },
        {
          question: 'AQERA est-elle déjà disponible ?',
          answer: 'Oui ! AQERA est maintenant disponible. Inscrivez-vous sur notre plateforme et commencez votre essai gratuit de 7 jours dès aujourd\'hui.'
        }
      ]
    },
    
    // CTA
    cta: {
      headline: 'Le marketing n\'a pas besoin de plus de bruit.',
      subheadline: 'Il a besoin de plus de valeur.',
      description: 'Rejoignez AQERA et découvrez une nouvelle manière de connecter les budgets des marques, les actions des utilisateurs et la croissance locale.',
      button: 'S\'inscrire et commencer l\'essai gratuit',
      dashboard: 'Accéder au dashboard'
    },
    
    // Footer
    footer: {
      description: 'La nouvelle infrastructure du marketing local.',
      navigation: 'Navigation',
      legal: 'Légal',
      social: 'Suivez-nous',
      privacy: 'Politique de confidentialité',
      terms: 'Conditions d\'utilisation',
      contact: 'Contact',
      copyright: '© 2025 AQERA. Tous droits réservés.'
    }
  },
  
  en: {
    // Navigation
    nav: {
      solution: 'Solution',
      howItWorks: 'How it works',
      forBrands: 'For brands',
      forUsers: 'For users',
      pricing: 'Pricing',
      faq: 'FAQ'
    },
    
    // Hero
    hero: {
      headline: 'Turn every marketing dollar into measurable action.',
      subheadline: 'Brands fund missions. Users earn money. AQERA transforms marketing budgets into real, measurable, and rewarded actions.',
      cta: 'Sign up and start free trial',
      credibility: 'Already adopted by cafés, restaurants, retail stores, beauty salons and many more.'
    },
    
    // Problem
    problem: {
      title: 'What if brands stopped wasting money?',
      subtitle: 'Traditional marketing is an obsolete model',
      items: [
        {
          title: 'No guarantee of results',
          description: 'Businesses pay for ads without ever knowing if they generate real actions.',
          solutionTitle: 'Pay for performance',
          solution: 'Payment only for validated and measurable actions.'
        },
        {
          title: 'Non-existent traceability',
          description: 'Campaigns lack precise data on actual consumer engagement.',
          solutionTitle: 'Total traceability',
          solution: 'Every action is traceable, verifiable and quantifiable in real-time.'
        },
        {
          title: 'Superficial engagement',
          description: 'Impressions and clicks don\'t translate into useful behaviors.',
          solutionTitle: 'Real engagement',
          solution: 'Concrete missions that generate real interactions.'
        },
        {
          title: 'Undistributed value',
          description: 'Consumers see ads without ever being truly valued for their attention.',
          solutionTitle: 'Shared value',
          solution: 'Users are directly rewarded for their actions.'
        }
      ]
    },
    
    // Solution
    solution: {
      title: 'AQERA transforms marketing budget into real value.',
      subtitle: 'A new economic infrastructure',
      description: 'We connect brand advertising budgets to consumer actions, creating a smarter local economic loop.',
      highlight: 'Brands pay for visibility. AQERA pays users for real actions.'
    },
    
    // How it works
    howItWorks: {
      title: 'A simple loop. A real impact.',
      subtitle: 'How AQERA works',
      steps: [
        {
          number: '01',
          title: 'Budget allocated',
          description: 'A café, restaurant, beauty salon or local business sets their gift card budget on AQERA.'
        },
        {
          number: '02',
          title: 'Missions created',
          description: 'AQERA transforms this budget into targeted, measurable marketing missions.'
        },
        {
          number: '03',
          title: 'Actions completed',
          description: 'Users perform useful and verifiable marketing actions.'
        },
        {
          number: '04',
          title: 'Money distributed',
          description: 'Users receive money directly in their AQERA wallet.'
        },
        {
          number: '05',
          title: 'Local value',
          description: 'They use their earnings in the partner gift card marketplace.'
        }
      ]
    },
    
    // Revolutionary
    revolutionary: {
      title: 'AQERA doesn\'t just broadcast marketing.',
      titleHighlight: 'AQERA makes it happen.',
      subtitle: 'A paradigm shift',
      comparison: {
        traditional: 'Traditional advertising',
        aqera: 'AQERA',
        items: [
          { traditional: 'Pay before results', aqera: 'Performance-based budget' },
          { traditional: 'Unclear visibility', aqera: 'Traceable actions' },
          { traditional: 'Passive audience', aqera: 'Active participation' },
          { traditional: 'Lost value', aqera: 'Distributed rewards' },
          { traditional: 'Linear spending', aqera: 'Local economic loop' }
        ]
      }
    },
    
    // Dual Audience
    dualAudience: {
      consumers: {
        title: 'For users',
        headline: 'Turn your attention into value.',
        benefits: [
          'Complete simple missions',
          'Earn real money',
          'Access useful rewards',
          'Use your earnings in a local marketplace',
          'Participate in a smarter economy'
        ]
      },
      brands: {
        title: 'For brands',
        headline: 'Turn your marketing into measurable results.',
        benefits: [
          'Activate a local audience',
          'Reward useful actions',
          'Control your budgets',
          'Measure performance in real-time',
          'Generate traffic, visibility and conversion'
        ]
      }
    },
    
    // Use Case
    useCase: {
      title: 'A simple loop. A real impact.',
      subtitle: 'A concrete example',
      steps: [
        'A business allocates a gift card budget (e.g., $400)',
        'AQERA creates paid missions',
        'Users perform concrete actions',
        'They receive money in their wallet',
        'They use this value at partner businesses'
      ],
      results: {
        title: 'Result',
        metrics: [
          '40 gift cards of $10',
          '120 actions completed across social networks',
          'Dozens of customers exposed and engaged'
        ],
        benefits: [
          'Real visibility',
          'Measurable engagement',
          'Local traffic'
        ]
      }
    },
    
    // Product
    product: {
      title: 'A complete technology platform.',
      subtitle: 'Discover the AQERA interface',
      modules: {
        missions: {
          title: 'Missions',
          description: 'Discover and complete paid marketing missions.'
        },
        wallet: {
          title: 'Wallet',
          description: 'Track your earnings and manage your money in real-time.'
        },
        history: {
          title: 'History',
          description: 'View all your completed missions and transactions.'
        },
        marketplace: {
          title: 'Marketplace',
          description: 'Use your earnings in our partner network.'
        }
      }
    },
    
    // Advantages
    advantages: {
      title: 'Why AQERA can change local marketing.',
      subtitle: 'Our strategic advantages',
      items: [
        { title: 'Measurable', description: 'Every action is traceable and quantifiable.' },
        { title: 'Scalable', description: 'Infrastructure designed for growth.' },
        { title: 'Transparent', description: 'Total visibility on budget usage.' },
        { title: 'Engaged', description: 'Active users, not spectators.' },
        { title: 'Local-first', description: 'Designed for the proximity economy.' },
        { title: 'Performance-driven', description: 'Concrete results, not promises.' }
      ]
    },
    
    // Vision
    vision: {
      title: 'The next generation of local marketing.',
      description: 'AQERA aims to redefine the relationship between advertising budgets, consumer behaviors, and local commerce. The ambition is to transform passive marketing spending into a smarter, fairer, and more measurable growth engine.'
    },
    
    // Pricing
    pricing: {
      title: 'Simple and transparent pricing.',
      subtitle: 'For brands and restaurants',
      price: '49',
      currency: 'CAD',
      period: '/month',
      trial: '7-day free trial',
      features: [
        'Full platform access',
        'Unlimited mission creation',
        'Advanced analytics dashboard',
        'Priority support',
        'No commitment'
      ],
      cta: 'Start free trial'
    },
    
    // FAQ
    faq: {
      title: 'Frequently asked questions',
      subtitle: 'Everything you need to know',
      items: [
        {
          question: 'Is AQERA a classic cashback app?',
          answer: 'No. AQERA is not a cashback or coupon app. It\'s a marketing infrastructure that transforms advertising budgets into paid missions. Users are rewarded for useful marketing actions, not simple purchases.'
        },
        {
          question: 'How do users earn money?',
          answer: 'Users complete marketing missions created by partner brands. Each validated mission generates a real money reward, directly credited to their AQERA wallet.'
        },
        {
          question: 'How do businesses fund the rewards?',
          answer: 'Businesses allocate a marketing budget on AQERA. This budget is transformed into missions. They only pay for actions actually performed and validated.'
        },
        {
          question: 'What is the wallet for?',
          answer: 'The AQERA wallet centralizes all user earnings. It\'s a digital wallet where money earned through missions accumulates.'
        },
        {
          question: 'How does the marketplace work?',
          answer: 'The marketplace allows users to use their earnings to buy gift cards from our local partners. This creates a virtuous economic loop within the ecosystem.'
        },
        {
          question: 'Why is AQERA different from traditional advertising?',
          answer: 'Traditional advertising pays for visibility without guaranteed results. AQERA only pays for measurable and validated actions. Budget becomes performance.'
        },
        {
          question: 'Who is AQERA for?',
          answer: 'AQERA is for cafés, restaurants, retail stores, grocery stores, beauty salons, barbers and all local businesses who want to optimize their marketing budget. And for consumers who want to be rewarded for their engagement.'
        },
        {
          question: 'Is AQERA already available?',
          answer: 'Yes! AQERA is now available. Sign up on our platform and start your 7-day free trial today.'
        }
      ]
    },
    
    // CTA
    cta: {
      headline: 'Marketing doesn\'t need more noise.',
      subheadline: 'It needs more value.',
      description: 'Join AQERA and discover a new way to connect brand budgets, user actions, and local growth.',
      button: 'Sign up and start free trial',
      dashboard: 'Access dashboard'
    },
    
    // Footer
    footer: {
      description: 'The new infrastructure of local marketing.',
      navigation: 'Navigation',
      legal: 'Legal',
      social: 'Follow us',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      contact: 'Contact',
      copyright: '© 2025 AQERA. All rights reserved.'
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('fr');
  
  useEffect(() => {
    const saved = localStorage.getItem('aqera-language');
    if (saved && (saved === 'fr' || saved === 'en')) {
      setLanguage(saved);
    }
  }, []);
  
  const toggleLanguage = () => {
    const newLang = language === 'fr' ? 'en' : 'fr';
    setLanguage(newLang);
    localStorage.setItem('aqera-language', newLang);
  };
  
  const t = translations[language];
  
  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
